import React, { useState, useEffect } from 'react';
import { Image, Trash2, Calendar, Send, AlertTriangle, Loader2, Search, Filter, MoreHorizontal } from 'lucide-react';
import { Button } from '../ui/button';
import { ANGLE_LABELS } from '../../data/pinTemplates';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const getAuth = () => ({});

const STATUS_LABELS = {
  draft: { label: 'Draft', color: 'bg-stone-100 text-stone-600' },
  ready: { label: 'Klar', color: 'bg-blue-50 text-blue-600' },
  scheduled: { label: 'Planlagt', color: 'bg-amber-50 text-amber-600' },
  publishing: { label: 'Publishing...', color: 'bg-yellow-50 text-yellow-600' },
  published: { label: 'Published', color: 'bg-green-50 text-green-600' },
  failed: { label: 'Failed', color: 'bg-red-50 text-red-600' },
};

const PinGallery = ({ filter, onEditPin, onRefresh, pinterestStatus }) => {
  const [pins, setPins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchPins = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter === 'scheduled') params.status = 'scheduled';
      if (filter === 'published') params.status = 'published';
      const res = await axios.get(`${API}/api/pin-studio/pins`, { ...getAuth(), params });
      setPins(res.data.pins || []);
    } catch (e) {
      console.error('Fetch pins error:', e);
    }
    setLoading(false);
  };

  useEffect(() => { fetchPins(); }, [filter]);

  const handleDelete = async (pinId, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this pin?')) return;
    try {
      await axios.delete(`${API}/api/pin-studio/pins/${pinId}`, getAuth());
      setPins((p) => p.filter((pin) => pin.id !== pinId));
      onRefresh?.();
    } catch (e) {
      console.error('Delete error:', e);
    }
  };

  const handleRetry = async (pinId, e) => {
    e.stopPropagation();
    try {
      await axios.put(`${API}/api/pin-studio/pins/${pinId}`, { status: 'draft' }, getAuth());
      onEditPin(pinId);
    } catch (e) {
      console.error('Retry error:', e);
    }
  };

  const filtered = pins.filter((p) =>
    !searchTerm || p.headline?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.pinterest_title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.source_product_title?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const title = filter === 'scheduled' ? 'Scheduled Pins' : filter === 'published' ? 'Published Pins' : 'My Pins';

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-5 h-5 animate-spin text-[#B49A88]" />
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="pin-gallery">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-serif font-bold text-stone-800 text-xl">{title}</h2>
          <p className="font-sans text-xs text-stone-400 mt-0.5">{filtered.length} pins</p>
        </div>
      </div>

      {/* Search */}
      {pins.length > 0 && (
        <div className="relative">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-300" />
          <input
            data-testid="pin-search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search pins..."
            className="w-full pl-10 pr-4 py-2.5 bg-white border border-stone-100 rounded-xl text-sm font-sans text-stone-700 placeholder:text-stone-300 focus:outline-none focus:border-[#B49A88] transition-colors"
          />
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="bg-white rounded-2xl border border-stone-100 p-12 text-center">
          <Image className="w-8 h-8 text-stone-200 mx-auto mb-3" />
          <p className="font-sans text-sm text-stone-500">No pins yet</p>
          <p className="font-sans text-xs text-stone-400 mt-1">Go to "Create Pins" to get started</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((pin) => {
            const angleInfo = ANGLE_LABELS[pin.marketing_angle] || { label: pin.marketing_angle || '—', color: '#888' };
            const statusInfo = STATUS_LABELS[pin.status] || STATUS_LABELS.draft;
            return (
              <div
                key={pin.id}
                className="bg-white rounded-2xl border border-stone-100 overflow-hidden hover:shadow-md hover:-translate-y-0.5 transition-all group cursor-pointer"
                onClick={() => onEditPin(pin.id)}
                data-testid={`pin-card-${pin.id}`}
              >
                <div className="aspect-[2/3] bg-gradient-to-b from-stone-50 to-stone-100 p-5 flex flex-col justify-center items-center text-center relative">
                  <span
                    className="absolute top-3 left-3 text-[9px] font-sans font-bold px-2 py-1 rounded-full"
                    style={{ background: angleInfo.color + '15', color: angleInfo.color }}
                  >
                    {angleInfo.label}
                  </span>
                  <p className="font-serif font-bold text-stone-800 text-base leading-tight mb-2">{pin.headline || 'Untitled'}</p>
                  {pin.subheadline && <p className="font-sans text-stone-500 text-[10px] leading-relaxed mb-2">{pin.subheadline}</p>}
                  {pin.cta && <span className="px-3.5 py-1 bg-[#B49A88] text-white text-[9px] font-sans font-semibold rounded-full">{pin.cta}</span>}
                </div>
                <div className="p-3">
                  <p className="font-sans font-medium text-stone-700 text-xs truncate">{pin.pinterest_title || pin.headline}</p>
                  <div className="flex items-center justify-between mt-2 pt-2 border-t border-stone-50">
                    <span className={`text-[9px] font-sans font-semibold px-2 py-0.5 rounded-full ${statusInfo.color}`}>
                      {statusInfo.label}
                    </span>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      {pin.status === 'failed' && (
                        <button onClick={(e) => handleRetry(pin.id, e)} className="text-[9px] font-sans font-semibold text-[#E60023] hover:underline">
                          Try again
                        </button>
                      )}
                      <button
                        onClick={(e) => handleDelete(pin.id, e)}
                        className="p-1 rounded-lg hover:bg-red-50 text-stone-300 hover:text-red-500 transition-colors"
                        data-testid={`delete-pin-${pin.id}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                  {pin.source_product_title && (
                    <p className="font-sans text-[9px] text-stone-300 mt-1 truncate">Fra: {pin.source_product_title}</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PinGallery;
